import { IBookmark, IWorkspace } from '~/types/model.types';
import {
    v2WorkspacePath,
    v2WorkspaceSendMessagePath,
    v2WorkspacesPipelinePath
} from '~/utils/ts-routes.utils';
import {
    ICreateFilePayload,
    ICreateWorkspaceFileResponse,
    IFetchPipelineRequest,
    IPaymentsForWorkspaceResponse,
    ISavePreferredVendorsListResponse,
    IWorkspaceMembersResponse,
    TBlockWorkspacePayload,
    TBlockWorkspaceResponse,
    IUserSummaryResponse,
    IWorkspaceResponse,
    IProjectSearchResponse
} from '~/types/server.types';
import { BaseHttpAdapter } from '~/services/adapters/base-http.adapter';
import { IMeetingsResponse } from '~/components/Contacts/server-types';
import { Flow } from '~/models/flow.model';
import {
    TEventDetails,
    TGetConflictingEventsResponse,
    TAddEventWorkspaceResponse,
    TWorkspaceFeedResponse,
    TChangeWorkspacePipelineStageResponse,
    TDisableWorkspaceTrackingInPipelineResponse,
    TUpdateWorkspaceNameResponse,
    TDismissPVLRequestResponse,
    IWorkspaceAttachmentsPayloadItem,
    TWorkspaceAttachmentsResponse
} from '~/apps/main/Workspace/server-types';
import { TTaskGroup } from '~/apps/main/Workspace/TasksTab/workspace-tasks.types';
import { WorkspaceType } from '~/types/enums.types';
import { ISendWorkspaceMessageTaskData } from '~/common/hooks/ClientPendingTask/client-pending-task.types';
import { TPvlData } from '~/components/FeedMessageEditor/PVL/Pvl.types';
import { GetWorkspaceDataParams } from '~/apps/extend/src/types/actions';
import { IApiError } from '~/services/interfaces/IApiService';

export class WorkspacesAdapter extends BaseHttpAdapter {
    fetchWorkspace(workspaceId: string) {
        return this.apiService.apiRequest<IWorkspace>({
            method: 'get',
            url: v2WorkspacePath(workspaceId)
        });
    }

    fetchExtWorkspace(details: {
        workspaceId: string;
        userId: string;
        companyId: string;
    }) {
        return this.apiService.apiRequest<IWorkspace>({
            method: 'get',
            url: `/api/v2/ext/workspaces/${details.workspaceId}`,
            params: { ctxu: details.userId, ctxc: details.companyId }
        });
    }

    fetchWorkspacesData(details: GetWorkspaceDataParams): Promise<
        {
            contact: IUserSummaryResponse;
            thread_workspace: IWorkspaceResponse;
            contact_workspaces: IProjectSearchResponse[];
        } & Partial<IApiError>
    > {
        const requestId = details.contactEmail
            ? `${details.threadId}|${details.contactEmail.toLowerCase()}`
            : details.threadId;

        return this.apiService.apiRequest<
            {
                contact: IUserSummaryResponse;
                thread_workspace: IWorkspaceResponse;
                contact_workspaces: IProjectSearchResponse[];
            } & Partial<IApiError>
        >({
            method: 'get',
            url: '/api/v2/ext/workspaces',
            data: {
                id: requestId
            },
            params: {
                contact_email: details.contactEmail,
                thread_id: details.threadId,
                object_mapping_id: details.objectMappingId,
                ctxu: details.userId,
                ctxc: details.companyId
            }
        });
    }

    fetchPipelineWorkspaces<T>(params: IFetchPipelineRequest) {
        return this.apiService.apiRequest<T>({
            method: 'GET',
            url: v2WorkspacesPipelinePath(),
            params
        });
    }

    fetchAllMeetings(workspaceId: string) {
        return this.apiService.apiRequest<IMeetingsResponse>({
            method: 'get',
            url: `/api/v2/workspaces/${workspaceId}/all_meetings`
        });
    }

    fetchWorkspaceMembers(workspaceId: string) {
        return this.apiService.apiRequest<IWorkspaceMembersResponse>({
            method: 'get',
            url: `/api/v2/workspaces/${workspaceId}/members`
        });
    }

    getEligibleMarkAsCompleteFlow(workspaceId: string) {
        return this.apiService.apiRequest<{ flow: Flow }>({
            method: 'get',
            url: `/api/v2/workspaces/${workspaceId}/eligible_mark_as_complete_flows`
        });
    }

    isDeletable(workspaceId: string) {
        return this.apiService.apiRequest<{ is_deletable: boolean }>({
            method: 'get',
            url: `/api/v2/workspaces/${workspaceId}/is_deletable`
        });
    }

    deleteWorkspace(workspaceId: string) {
        return this.apiService.apiRequest<void>({
            method: 'delete',
            url: `/api/v2/workspaces/${workspaceId}`
        });
    }

    blockWorkspace(workspaceId: string, payload: TBlockWorkspacePayload) {
        return this.apiService.apiRequest<TBlockWorkspaceResponse>({
            method: 'put',
            url: `/api/v2/workspaces/${workspaceId}/block`,
            data: payload,
            networkLabel: 'blockWorkspace'
        });
    }

    saveArchiveReason(companyId: string, reason: string) {
        return this.apiService.apiRequest<void>({
            method: 'post',
            url: `/api/v2/companies/${companyId}/save_archive_reason`,
            data: { reason }
        });
    }

    deleteArchiveReason(companyId: string, reason: string) {
        return this.apiService.apiRequest<void>({
            method: 'delete',
            url: `/api/v2/companies/${companyId}/delete_archive_reason`,
            data: { reason }
        });
    }

    deleteActivityItem(activityItemId: string, workspaceId: string) {
        return this.apiService.apiRequest<void>({
            method: 'DELETE',
            url: `/api/v2/workspaces/${workspaceId}/activityItem/${activityItemId}`
        });
    }

    // TODO: move this
    getEventDetails(eventId: string) {
        return this.apiService.apiRequest<TEventDetails>({
            method: 'get',
            url: `/api/v2/events/${eventId}/details`,
            networkLabel: 'getEventDetails'
        });
    }

    getConflictingEvents(eventId: string) {
        return this.apiService.apiRequest<TGetConflictingEventsResponse>({
            method: 'get',
            url: `/api/v2/events/${eventId}/conflicting_events`
        });
    }

    updateConflictPvlAction(eventId: string, hide: boolean) {
        return this.apiService.apiRequest<{
            hide_conflict_pvl_action: boolean;
        }>({
            method: 'put',
            url: `/api/v2/events/${eventId}/conflict_pvl_action`,
            data: {
                hide
            }
        });
    }

    addWorkspaceToEvent(
        eventId: string,
        workspaceType: Exclude<WorkspaceType, 'timeline'>
    ) {
        return this.apiService.apiRequest<TAddEventWorkspaceResponse>({
            method: 'post',
            url: `/api/v2/events/${eventId}/workspaces`,
            data: {
                workspace_type: workspaceType
            }
        });
    }

    hideAddMemberRelatedToSessionsBanner(workspaceId: string) {
        return this.apiService.apiRequest<{
            sessions_with_unrelated_member: IWorkspace['sessions_with_unrelated_member'];
        }>({
            method: 'put',
            url: `/api/v2/workspaces/${workspaceId}/remove_sessions_with_unrelated_member`
        });
    }

    updateTeamMembersInWorkspace(
        workspaceId: string,
        addedMemberIDs?: string[],
        removedMemberIDs?: string[]
    ) {
        const data = {
            added_members: addedMemberIDs,
            removed_members: removedMemberIDs
        };
        return this.apiService.apiRequest<{
            sessions_with_unrelated_member: IWorkspace['sessions_with_unrelated_member'];
        }>({
            method: 'put',
            url: `/api/v2/workspaces/${workspaceId}/team_members`,
            data: data
        });
    }

    getWorkspaceFeed(workspaceId: string) {
        return this.apiService.apiRequest<TWorkspaceFeedResponse>({
            method: 'get',
            url: `/api/v2/workspaces/${workspaceId}/feed`
        });
    }

    markFeedItemsAsSeen(workspaceId: string, itemIds: string[]) {
        return this.apiService.apiRequest({
            method: 'put',
            url: `/api/v2/workspaces/${workspaceId}/feed_items/seen`,
            data: {
                item_ids: itemIds
            }
        });
    }

    changeWorkspacePipelineStage(
        workspaceId: string,
        stageId: string,
        disableTracking: boolean | null = null
    ) {
        return this.apiService.apiRequest<TChangeWorkspacePipelineStageResponse>(
            {
                method: 'PUT',
                url: `/api/v2/workspaces/${workspaceId}/stages/${stageId}`,
                data: { disable_tracking: disableTracking },
                networkLabel: 'changeWorkspacePipelineStage'
            }
        );
    }

    disableWorkspaceTrackingInPipeline(
        workspaceId: string,
        disableTracking: boolean
    ) {
        return this.apiService.apiRequest<TDisableWorkspaceTrackingInPipelineResponse>(
            {
                method: 'PUT',
                url: `/api/v2/workspaces/${workspaceId}/pipeline`,
                data: { disable_tracking: disableTracking },
                networkLabel: 'disableWorkspaceTrackingInPipeline'
            }
        );
    }

    updateWorkspaceName(workspaceId: string, name: string) {
        return this.apiService.apiRequest<TUpdateWorkspaceNameResponse>({
            method: 'put',
            url: `/api/v2/workspaces/${workspaceId}/tab_name`,
            data: { name }
        });
    }

    dismissPVLRequest(workspaceId: string, pvlId: string) {
        return this.apiService.apiRequest<TDismissPVLRequestResponse>({
            method: 'put',
            url: `/api/v2/workspaces/${workspaceId}/preferred_vendors_list`,
            data: { pvl_id: pvlId }
        });
    }

    removeMember(workspaceId: string, memberId: string) {
        return this.apiService.apiRequest<void>({
            method: 'delete',
            url: `/api/v2/workspaces/${workspaceId}/members/${memberId}`
        });
    }

    addAttachments(
        workspaceId: string,
        attachments: IWorkspaceAttachmentsPayloadItem[]
    ) {
        return this.apiService.apiRequest<TWorkspaceAttachmentsResponse>({
            method: 'post',
            url: `/api/v2/workspaces/${workspaceId}/attachments`,
            data: { attachments }
        });
    }

    updateAttachment(
        workspaceId: string,
        attachment: IWorkspaceAttachmentsPayloadItem & { id: string }
    ) {
        return this.apiService.apiRequest<IBookmark>({
            method: 'PUT',
            url: `/api/v2/workspaces/${workspaceId}/attachments/${attachment.id}`,
            data: attachment
        });
    }

    deleteAttachment({
        workspaceId,
        attachmentId
    }: {
        workspaceId: string;
        attachmentId: string;
    }) {
        return this.apiService.apiRequest<void>({
            method: 'DELETE',
            url: `/api/v2/workspaces/${workspaceId}/attachments/${attachmentId}`
        });
    }

    getPaymentsForWorkspace(workspaceId: string) {
        return this.apiService.apiRequest<IPaymentsForWorkspaceResponse>({
            method: 'GET',
            url: `/api/v2/workspaces/${workspaceId}/payments`,
            networkLabel: 'getPaymentsForWorkspace'
        });
    }

    getAttachments(workspaceId: string) {
        return this.apiService.apiRequest<TWorkspaceAttachmentsResponse>({
            method: 'GET',
            url: `/api/v2/workspaces/${workspaceId}/attachments`,
            networkLabel: 'getAttachments'
        });
    }

    getTaskGroups(workspaceId: string) {
        return this.apiService.apiRequest<TTaskGroup[]>({
            method: 'GET',
            url: `/api/v2/workspaces/${workspaceId}/taskgroup`
        });
    }

    createTaskGroup({
        workspaceId,
        taskGroupData
    }: {
        workspaceId: string;
        taskGroupData: Pick<TTaskGroup, 'title' | 'color'>;
    }) {
        return this.apiService.apiRequest<TTaskGroup>({
            method: 'POST',
            url: `/api/v2/workspaces/${workspaceId}/taskgroup`,
            data: taskGroupData
        });
    }

    updateTaskGroup({
        workspaceId,
        taskGroupId,
        taskGroupData: { title, color }
    }: {
        workspaceId: string;
        taskGroupId: string;
        taskGroupData: Pick<TTaskGroup, 'title' | 'color'>;
    }) {
        return this.apiService.apiRequest<TTaskGroup>({
            method: 'PUT',
            url: `/api/v2/workspaces/${workspaceId}/taskgroup/${taskGroupId}`,
            data: { title, color }
        });
    }

    deleteTaskGroup({
        workspaceId,
        taskGroupId
    }: {
        workspaceId: string;
        taskGroupId: string;
    }) {
        return this.apiService.apiRequest<void>({
            method: 'DELETE',
            url: `/api/v2/workspaces/${workspaceId}/taskgroup/${taskGroupId}`
        });
    }

    async sendEmail(data: ISendWorkspaceMessageTaskData) {
        return this.apiService.apiRequest<any>({
            method: 'POST',
            url: v2WorkspaceSendMessagePath(data.ws_id, data)
        });
    }

    inviteToWorkspace(workspaceId: string, memberId: string) {
        return this.apiService.apiRequest<void>({
            method: 'put',
            url: `/api/v2/workspaces/${workspaceId}/invite/${memberId}`
        });
    }

    createWorkspaceFile(
        workspaceId: string,
        { fileType, templateId, baseFileId }: ICreateFilePayload,
        extraParams?: object
    ) {
        return this.apiService.apiRequest<ICreateWorkspaceFileResponse>({
            method: 'post',
            url: `/api/v2/workspaces/${workspaceId}/workspace_files`,
            data: {
                file_type: fileType,
                template_id: templateId,
                base_file_id: baseFileId,
                ...(extraParams || {})
            }
        });
    }

    updatePreferredVendorsList(workspaceId: string, data: TPvlData) {
        return this.apiService.apiRequest<ISavePreferredVendorsListResponse>({
            method: 'PUT',
            url: `/api/v2/workspaces/${workspaceId}/preferred_vendors_lists`,
            data
        });
    }

    savePreferredVendorsList(workspaceId: string, data: TPvlData) {
        return this.apiService.apiRequest<ISavePreferredVendorsListResponse>({
            method: 'POST',
            url: `/api/v2/workspaces/${workspaceId}/preferred_vendors_lists`,
            data
        });
    }

    async getDefaultVendorReferralEmail(workspaceId: string) {
        type TDefaultVendorReferralEmailResponse = {
            vendor_referral_email: { html_body: string; subject: string };
        };

        return this.apiService.apiRequest<TDefaultVendorReferralEmailResponse>({
            method: 'GET',
            url: `/api/v2/workspaces/${workspaceId}/referral_email`,
            networkLabel: 'getDefaultVendorReferralEmail'
        });
    }

    sendReferralEmail(workspaceId: string, userId: string, data: any) {
        return this.apiService.apiRequest<void>({
            method: 'POST',
            url: `/api/v2/workspaces/${workspaceId}/user/${userId}/referrals`,
            data
        });
    }
}
